
import StandardReturnsContactModal from "@dana/Components/StandardReturnsContactModal/StandardReturnsContactModal";
import RMADetailsView from "@dana/Components/StandardReturnsOrderStatus/RMADetailsView";
import { themeColors } from "@dana/MobiusOverrides/styles/colors";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import getStandardReturnOrderDetails from "@dana/Store/Data/StandardReturnsStatus/Handlers/StandardReturnOrderDetailsHandler";
import showContactModal from "@dana/Store/Pages/AnnualReturnsOrder/Handlers/ShowContactModal";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import Link from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// --- Props Interface ---
interface StepSubmitResultProps {
    onGenerateRma: () => Promise<void>;
}

// --- Styles Interfaces and Objects ---
export interface StepSubmitResultStyles {
    wrapper?: InjectableCss;
    statusFailed?: InjectableCss;
    statusSuccess?: InjectableCss;
    modal?: ModalPresentationProps;
}

export const StepSubmitResultStyles: StepSubmitResultStyles = {
    wrapper: {
        css: css`
            .text-red {
                color: ${themeColors.danger};
            }
        `,
    },
    statusFailed: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 16px;
            justify-content: center;
            align-items: center;
            padding: 24px 0;
        `,
    },
    statusSuccess: {
        css: css`
            text-align: center;
            .rma-info {
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                gap: 8px;
                padding: 32px;
                border: 2px dashed ${themeColors.primary.richBlack};
                margin: 32px auto;
                max-width: 700px;
            }

            .footer-text {
                margin-bottom: 32px;
            }
        `,
    },
    modal: {
        size: 1140,
        sizeVariant: "large",
        headlineTypographyProps: {
            variant: "h4",
            css: css`
                margin-bottom: 0;
            `,
        },
    },
};

const styles = StepSubmitResultStyles;
const mapDispatchToProps = {
    getStandardReturnOrderDetails,
    showContactModal,
};

const mapStateToProps = (state: ApplicationStateDana) => {
    const standardReturnPage = getPageLinkByPageType(state, "StandardReturnsPage");
    return {
        submitStatus: state.standardReturnState?.submitStatus,
        submitedOrderNumber: state.standardReturnState?.submitedOrderNumber,
        salseOrgDetails: state.standardReturnState?.salseOrgDetails,
        standardReturnPage

    };
};
type Props = ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps> & StepSubmitResultProps & HasHistory;
// --- Component Definition ---
const StepSubmitResult: FC<Props> = ({
    submitStatus,
    submitedOrderNumber,
    getStandardReturnOrderDetails,
    showContactModal,
    salseOrgDetails,
    history,
    standardReturnPage
}) => {
    const [isModalOpen, setModalOpen] = useState<boolean>(false);
    const [modalTitle, setModalTitle] = useState<string>("");
    const getStandardReturnOrderDetailsHandler = (orderNumber: string) => {       

        if (standardReturnPage) {
            history.push(standardReturnPage?.url);
        }
    };
    const closeModal = () => {
        setModalOpen(false);
    };
    const openContactModal = (e: React.MouseEvent) => {
        e.preventDefault();
        showContactModal({ showContactModal: true });
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            {submitStatus === "IDLE" && (
                <StyledWrapper>
                    {translate("Click")} <strong>{translate("Submit")}</strong>{" "}
                    {translate("on the previous step to send Return Authorization Request.")}
                </StyledWrapper>
            )}
            {submitStatus === "SUBMITTING" && <StyledWrapper>{translate("Submitting your request")}...</StyledWrapper>}
            {submitStatus === "FAILED" && (
                <StyledWrapper {...styles.statusFailed}>
                    <Typography as="p">
                        {translate("Return request is not accepted. Please contact your")}{" "}
                        <Link onClick={openContactModal}>{translate("Sales Representative")}</Link>{" "}
                        {translate("with any questions")}
                    </Typography>
                    <Button disabled>{translate("Generate RMA document")}</Button>
                </StyledWrapper>
            )}
            {submitStatus === "SUCCESS" && (
                <StyledWrapper {...styles.statusSuccess}>
                    <Typography as="p">
                        {translate("We received your return authorization request.")}{" "}
                        <Typography className="text-red">
                            {translate(
                                "Please write your RMA number on the outer carton of the box and all documents.",
                            )}
                        </Typography>{" "}
                    </Typography>
                    <Typography as="p">
                        {translate("Please do not mix or combine return shipments")}{" "}
                        <Typography className="text-red">
                            {translate(
                                "Credit is not approved until parts have been inspected and credit memo is issues.",
                            )}
                        </Typography>{" "}
                    </Typography>
                    <StyledWrapper className="rma-info">
                        <Typography as="p">
                            {submitedOrderNumber && (
                                <>
                                    {translate("RMA number")} : <strong>{submitedOrderNumber}</strong>
                                </>
                            )}
                        </Typography>
                        <Typography as="p">
                            {translate("Freight Responsibility")}: <strong>{translate("Customer")}</strong>
                        </Typography>
                        <Typography as="p">
                            {translate("Ship Return To")}:{" "}
                            <strong>
                               {salseOrgDetails?.orgName} {salseOrgDetails?.orgAddress}, {salseOrgDetails?.country}
                            </strong>
                        </Typography>
                    </StyledWrapper>
                    <Typography className="footer-text" as="p">
                        {translate("Please be sure to keep record of the return tracking information. Thank you!")}
                    </Typography>
                    {submitedOrderNumber && (
                        <Button onClick={() => getStandardReturnOrderDetailsHandler(submitedOrderNumber)}>
                            {translate("Generate RMA document")}
                        </Button>
                    )}
                    <Modal
                        headline={translate(modalTitle)}
                        isOpen={isModalOpen}
                        handleClose={closeModal}
                        {...styles.modal}
                    >
                        <RMADetailsView />
                    </Modal>

                </StyledWrapper>
            )}
            <StandardReturnsContactModal />
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(StepSubmitResult));
